import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { isNonNullable } from '@qtek/shared/utils';
import { EMPTY } from 'rxjs';
import { transformerFactory } from './transform';

/**
 * Update paginator component.
 *
 * @param paginator Instance of paginator to be updated.
 * @param event Propeties that will be set on paginator.
 */
export function updatePaginator(
  paginator: MatPaginator,
  event: Partial<PageEvent>
) {
  return Promise.resolve().then(() => {
    if (isNonNullable(event.length)) {
      paginator.length = event.length;
    }

    if (isNonNullable(event.pageIndex)) {
      paginator.pageIndex = event.pageIndex;
    }

    if (isNonNullable(event.pageSize)) {
      paginator.pageSize = event.pageSize;
    }
  });
}

/**
 * Splice data based on {@link MatPaginator} config.
 *
 * @param data Array to paginate.
 * @param paginator Instance of {@link MatPaginator}.
 * @param shouldPaginatorUpdate Wheter to update paginator properties.
 */
export function pageData<T>(
  data: T[],
  paginator: MatPaginator,
  shouldPaginatorUpdate?: boolean
): T[] {
  if (!paginator) {
    return data;
  }

  if (shouldPaginatorUpdate) {
    updatePaginator(paginator, {
      length: data.length,
    });
  }

  let startIndex = paginator.pageIndex * paginator.pageSize;

  /** If start index is greater then length of data, we need to change page to 0. */
  if (startIndex > data.length) {
    startIndex = 0;

    updatePaginator(paginator, {
      pageIndex: 0,
    }).then(() => {
      /** TODO: Check if we can do better then emitting new page event(this is done to update url params). */
      paginator.page.emit({
        length: paginator.length,
        pageIndex: 0,
        pageSize: paginator.pageSize,
      });
    });
  }

  return data.slice().splice(startIndex, paginator.pageSize);
}

/**
 * Paginate data be {@link MatPaginator} config.
 *
 * @param paginator Instance of paginator to get events from.
 * @param shouldPaginatorUpdate Determines if paginator will be updated.
 */
export function paginate<T>(
  paginator: MatPaginator,
  shouldPaginatorUpdate?: boolean
) {
  return transformerFactory<T, PageEvent>(
    paginator ? paginator.page : EMPTY,
    ([data]) => pageData<T>(data, paginator, shouldPaginatorUpdate)
  );
}
